// src/pages/user-pages/StudentProfile.js
import { useState, useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

// Assets
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import transactionsIcon from "../../assets/transactions_icon.png";
import deliveryIcon from "../../assets/logistics-delivery_icon.png";
import levelUp from "../../assets/changes.png";
import evaluation from "../../assets/writing.png";

// Contexts and Utilities
import { UserAuth } from "../../utils/AuthContext";
import useLanguage from "../../utils/useLanguage";

// Components
import BackToDashboard from "../../components/BackToDashboard";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";
import CustomButton from "../../components/CustomButton";
import InformationBox from "../../components/InformationBox";
import BalanceBoxes from "../../components/BalanceBoxes";

// Styles
import "../InvoicesManagement.scss";
import TextDiv from "../../components/TextDiv";
import AreYouSureModal from "../../components/AreYouSureModal";
import {
  changeStudentClass,
  getStudentTransactions,
} from "../../services/StudentServices";
import AddAcademicYear from "./AddAcademicYear";
import StudentTransactionModal from "./StudentTransactionModal";
import StudentEvaluationModal from "./StudentEvaluationModal"; // ADD THIS IMPORT
import { RedFlagIndicator } from "../../components/RedFlagIndicator";

const StudentProfile = () => {
  // ==============================
  // 1. Contexts and Translations
  // ==============================
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { id } = useParams();
  const { currentDirection } = useLanguage();
  // const { loading, setLoading } = useContext(DataContext);
  const [loading, setLoading] = useState(true);

  // ==============================
  // 2. State Management
  // ==============================
  // Student Records Data
  const [studentRecords, setStudentRecords] = useState([]);
  const [totalAmount, setTotalAmount] = useState(null);
  const [recordsTotalPages, setRecordsTotalPages] = useState(0);
  const [recordsTotalRecords, setRecordsTotalRecords] = useState(0);
  const [student, setStudent] = useState(null);
  const [currentEducationLevel, setCurrentEducationLevel] = useState(null);
  const [selectedEducationLevel, setSelectedEducationLevel] = useState({
    title: "",
    value: "",
  });
  const [showAreYouSureModal, setShowAreYouSureModal] = useState(false);
  const [showChangeClass, setShowChangeClass] = useState(false);
  const [showAddAcademicYear, setShowAddAcademicYear] = useState(false);
  const [showStudentEvaluation, setShowStudentEvaluation] = useState(false); // ADD THIS STATE
  const [transactionsModal, setTransactionsModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [shouldGetStudents, setShouldGetStudents] = useState(false);
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [isGraduate, setIsGraduate] = useState(false);

  // ==============================
  // 3. Constants and Configurations
  // ==============================
  const titles = useMemo(
    () => [
      { value: "educationLevel" },
      { value: "source" },
      { value: "registrationDate" },
      { value: "transactions" },
    ],
    []
  );

  // ==============================
  // 4. Event Handlers
  // ==============================

  const handleToggleTransactionsModal = (yearId) => {
    setTransactionsModal((prev) => !prev);
    setSelectedYearId(yearId);
    if (!transactionsModal) getAllStudentTransactions();
    // setSelectedStudent(student);
  };

  const handleChangeClass = () => {
    updateStudentClass();
    setShowChangeClass(false);
    setShowAreYouSureModal(false);
  };

  // ADD THIS HANDLER FOR STUDENT EVALUATION
  const handleToggleEvaluationModal = () => {
    setShowStudentEvaluation((prev) => !prev);
  };

  const handleEvaluationUpdated = () => {
    // You can refresh any data here if needed
    // For now, just close the modal
    getAllStudentTransactions();
    console.log("Student evaluation updated successfully");
  };

  // ==============================
  // 5. Data Loading Functions
  // ==============================

  const getAllStudentTransactions = async () => {
    try {
      setLoading(true);
      const response = await getStudentTransactions(id);

      const data = response.data;
      if (response.status === 200) {
        // Set student basic info using correct property names
        setStudent({
          id: data.id,
          name: data.fullName,
          code: data.studentCode,
          totalAmount: data.balance,
          studentEvaluations: data.studentEvaluations[0],
        });
        setSelectedStudent({
          id: data.id,
          fullName: data.fullName,
          studentCode: data.studentCode,
          totalAmount: data.balance,
        });
        setTotalAmount(data.balance);

        // Find the active student year to get current class
        const activeStudentYear =
          data.studentYears?.find((year) => year.active) ||
          data.studentYears?.[0];

        if (activeStudentYear?.schoolClass) {
          setCurrentEducationLevel(activeStudentYear.schoolClass);
          setSelectedEducationLevel({
            title: activeStudentYear.schoolClass.name,
            value: activeStudentYear.schoolClass.id,
          });
        }

        const activeYears = data?.studentYears?.filter((year) => year.active);
        setIsGraduate(() => activeYears.length > 0);

        // Transform studentYears data for table (since there's no separate transactions array)
        const formattedRecords = (data.studentYears || []).map((yearRecord) => [
          {
            type: TABLE_CELL_TYPES.TEXT,
            value: yearRecord.schoolClass?.name || "N/A",
          },
          {
            type: TABLE_CELL_TYPES.TEXT,
            value: t(yearRecord.source) || "N/A",
          },
          {
            type: TABLE_CELL_TYPES.DATE,
            value: yearRecord.registerDate
              ? new Date(yearRecord.registerDate)
              : yearRecord.createdAt
              ? new Date(yearRecord.createdAt)
              : new Date(),
          },
          {
            type: TABLE_CELL_TYPES.ELEMENT,
            value: (
              <CustomButton
                variant="blank"
                title={t("transactions")}
                onClick={() => handleToggleTransactionsModal(yearRecord.id)}
                icon={transactionsIcon}
                iconSize={25}
              />
            ),
          },
        ]);

        setStudentRecords(formattedRecords);
        setRecordsTotalPages(1);
        setRecordsTotalRecords(data.studentYears?.length || 0);
      }
    } catch (error) {
      console.error("Error fetching student transactions:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateStudentClass = async () => {
    try {
      const response = await changeStudentClass(id, {
        schoolClassId: selectedEducationLevel.value,
      });
      if (response.status === 200) {
        // Refresh data after successful update
        getAllStudentTransactions();
      }
    } catch (error) {
      console.error("Error updating student class:", error);
    }
  };

  // ==============================
  // 6. Side Effects
  // ==============================
  useEffect(() => {
    // Load student data and education levels
    getAllStudentTransactions();
  }, [id]);

  // ==============================
  // 7. Render
  // ==============================
  return (
    <>
      <main className="e-container">
        <div>
          <BackToDashboard
            title={t("back")}
            variant="blank"
            icon={currentDirection === "ltr" ? previousIcon : nextIcon}
            iconSize={14}
            to={userData?.userType === "user" ? "/invoiceManagement" : "/students"}
          />
        </div>

        <div className="invoice-table-container">
          <div className="user-section">
            <div className="user">
              <span className="user-text">{`${t("user")}:`}</span>
              <span className="user-text">
                {localStorage.getItem("username")}
              </span>
            </div>
          </div>
          {loading ? (
            <Loader size={LOADER_SIZES.MEDIUM} />
          ) : (
            <>
              <div className="u-margin--bottom_500 o-column o-column--gap-size_300">
                <TextDiv
                  title="studentName"
                  value={student?.name || "اسم الطالب"}
                  divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center"
                  pClasses="u-min-width-specific_150"
                />

                <TextDiv
                  title="studentId"
                  value={student?.code || "رمز الطالب"}
                  divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center"
                  pClasses="u-min-width-specific_150"
                />
                <TextDiv
                  title="academicLevel"
                  value={currentEducationLevel?.name || "N/A"}
                  divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center"
                  pClasses="u-min-width-specific_150"
                />

                <div className="o-row o-row--gap-size_200 o-row--vertical-align_center u-width_full-width">
                  <span className="u-width--min-width_150">
                    {t("redFlag")}:
                  </span>
                  <RedFlagIndicator
                    isRedFlag={student?.studentEvaluations?.redFlag}
                  />
                  <span
                    className="u-width--min-width_50 t-text-align_center"
                    style={{
                      color: student?.studentEvaluations?.redFlag
                        ? "#ff4444"
                        : "green",
                      fontWeight: student?.studentEvaluations?.redFlag
                        ? "bold"
                        : "normal",
                    }}
                  >
                    {student?.studentEvaluations?.redFlag ? t("yes") : t("no")}
                  </span>
                </div>
              </div>
              <div className="o-row o-row--horizontal-align_space-between o-row--gap-size_600 o-column-row-switcher-reversed">
                <CustomButton
                  title={t("supplies")}
                  iconSize={25}
                  icon={deliveryIcon}
                  to={`supplies`}
                  variant="blank"
                  elementType={Link}
                />
                <div className="o-row o-row--gap-size_400">
                  <CustomButton
                    title={t("studentEvaluation")}
                    icon={evaluation}
                    variant="blank"
                    iconSize={23}
                    onClick={handleToggleEvaluationModal}
                  />

                  {isGraduate && (
                    <CustomButton
                      title={t("changeStudentLevel")}
                      icon={levelUp}
                      iconSize={25}
                      onClick={() => setShowAddAcademicYear(true)}
                    />
                  )}
                </div>
              </div>

              {studentRecords.length > 0 ? (
                <>
                  <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end u-margin--bottom_400"></section>

                  <RoundedTable titles={titles} rows={studentRecords} />

                  {/* Remove pagination since we're showing all studentYears records */}
                  <section className="pagination">
                    <div className="pagination-buttons">
                      <span className="pagination-location">
                        {t("totalRecords")}: {recordsTotalRecords}
                      </span>
                    </div>
                  </section>

                  <div>
                    <BalanceBoxes
                      boxes={[
                        {
                          title: "balance",
                          value: `${totalAmount?.toLocaleString()} ${t("IQD")}`,
                        },
                      ]}
                    />
                  </div>
                </>
              ) : (
                <InformationBox>{t("noRecordsFound")}</InformationBox>
              )}
            </>
          )}
        </div>

        {/* EXISTING MODALS */}
        {showAreYouSureModal && (
          <AreYouSureModal
            onClose={() => setShowAreYouSureModal(false)}
            onConfirm={handleChangeClass}
            customMessage="areYouSureChangeClassMessage"
            closeTitle="cancel"
            confirmTitle="confirm"
            confirm={true}
            confirmButtonVariant={null}
            // loading={deleteLoading}
            confirmIconType="whiteCheck"

          />
        )}

        {transactionsModal && (
          <StudentTransactionModal
            selected={selectedStudent}
            handleToggleTransactionsModal={handleToggleTransactionsModal}
            setShouldGetStudents={setShouldGetStudents}
            yearId={selectedYearId}
          />
        )}

        <AddAcademicYear
          isOpen={showAddAcademicYear}
          onClose={() => setShowAddAcademicYear(false)}
          onAcademicYearAdded={getAllStudentTransactions} // Refresh student data
          studentId={id} // Pass the student ID from useParams
          studentInfo={selectedStudent}
        />

        {/* ADD THE NEW STUDENT EVALUATION MODAL */}
        <StudentEvaluationModal
          isOpen={showStudentEvaluation}
          onClose={() => setShowStudentEvaluation(false)}
          onEvaluationUpdated={handleEvaluationUpdated}
          studentId={id} // Pass the student ID from useParams
          studentInfo={{
            ...selectedStudent,
            schoolClass: { ...currentEducationLevel },
          }}
        />
      </main>
    </>
  );
};

export default StudentProfile;
